import { API_ENDPOINT } from "@/constants/appConstants";
import { sprWithJobIdFlow } from "@/constants/common";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useMobile } from "@/hooks";
import { Button, User } from "@monsterindia/global-ui-components";
import { useMemo } from "react";
import Lottie from "react-lottie-player";

import ProgressBar from "../registration/progress-bar";

const RegistrationHeader = () => {
  const search = window.location.search;
  let urlParams = new URLSearchParams(search);
  const isApp = urlParams.get("app");
  const isMobile = useMobile();
  const {
    screenType,
    hostname,
    isSkipabl,
    progressPercent,
    flowType,
    getNextAction,
  } = useRegistrationProps();
  const lottieUrl = window?._siteProps_?.propertiesMap?.logoUrl || "";

  // Memoize logo type and src to avoid flicker
  const logoProps = useMemo(() => {
    return lottieUrl?.includes(".json");
  }, [lottieUrl]);

  // Computed header state logic
  const headerState = useMemo(() => {
    // Prevent flash by ensuring we have a valid screenType
    const isLoaded = !!screenType;
    const isFirstScreen = screenType === "MOBILE_NUMBER_EMAIL";
    const hasProgress = !!progressPercent && progressPercent !== 100;
    const isSPRwithJobIdFlow = flowType === sprWithJobIdFlow;
    const shouldShowSkip =
      isLoaded && isSkipabl && !isFirstScreen && screenType !== "RESUME";

    return {
      isLoaded,
      isFirstScreen,
      hasProgress: isLoaded && hasProgress,
      shouldShowSkip,
      showLogo: isLoaded && !isApp,
      showLoginButton: isLoaded && isFirstScreen && !isSPRwithJobIdFlow,
    };
  }, [screenType, progressPercent, isSkipabl]);

  // Logo component
  const LogoComponent = useMemo(() => {
    if (!headerState.showLogo) return null;

    if (logoProps.isLottie) {
      return (
        <Lottie
          path={lottieUrl}
          loop
          play
          style={{
            height: isMobile ? "auto" : "34px",
            width: isMobile ? "90px" : "auto",
          }}
        />
      );
    }
    return (
      <img
        src={API_ENDPOINT(hostname).media.founditLogo}
        alt="Foundit Logo"
        className="h-[24px] w-[97px]"
      />
    );
  }, [headerState.showLogo, logoProps, isMobile]);

  // Login button component
  const LoginButton = () => {
    if (!headerState.showLoginButton) return null;

    return (
      <Button
        label="Login"
        modifier="secondary"
        url="/rio/login"
        leftIcon={<User width={20} height={20} />}
        onClick={() => {}}
        className="bg-white"
      />
    );
  };

  // Skip button component
  const SkipButton = () => {
    if (!headerState.shouldShowSkip) return null;

    return (
      <Button
        label="Skip"
        modifier="secondary"
        onClick={getNextAction}
        className="text-content-secondary border-border-moderate w-20 rounded-full bg-transparent text-center"
      />
    );
  };

  // Desktop progress bar with overlay positioning
  const DesktopProgressBar = () => {
    if (isMobile || !headerState.hasProgress) return null;

    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-6/12">
          <ProgressBar percent={progressPercent} />
        </div>
      </div>
    );
  };

  // Mobile progress bar
  const MobileProgressBar = () => {
    if (!isMobile || !headerState.hasProgress) return null;

    return (
      <div className="mt-6 px-4">
        <ProgressBar percent={progressPercent} />
      </div>
    );
  };

  // Prevent rendering header content until data is loaded
  if (!headerState.isLoaded) {
    return (
      <header className="z-50 w-full px-4">
        <div className="mx-auto mb-5 mt-12 flex w-full items-center justify-between md:mb-9 lg:w-[85%!important]">
          {/* Empty header while loading */}
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="z-50 w-full px-4">
        <div className="relative mx-auto mb-5 mt-12 flex w-full items-center justify-between md:mb-9 lg:w-[85%!important]">
          {/* Desktop progress bar overlay */}
          <DesktopProgressBar />

          {/* Left section - Logo */}
          <div className="relative z-10">{LogoComponent}</div>

          {/* Right section - Login or Skip button */}
          <div className="relative z-10">
            <LoginButton />
            <SkipButton />
          </div>
        </div>
      </header>

      {/* Mobile progress bar */}
      <MobileProgressBar />
    </>
  );
};

export default RegistrationHeader;
